<div [formGroup]="group" class="modern-input-wrapper">
  <!-- ARRAY -->
  <ng-container *ngIf="field.type === 'array'">
    <div
      class="flex items-center justify-between mb-3 p-3 bg-gradient-to-r from-primary-50/50 to-transparent rounded-lg border-l-4 border-primary-400"
    >
      <div>
        <div class="text-sm font-semibold text-neutral-900">{{ field.label }}</div>
        <div class="text-xs text-neutral-600 mt-0.5">{{ field.hint }}</div>
      </div>
    </div>

    <div
      *ngIf="arrayControl?.length === 0"
      class="py-6 px-4 bg-gradient-to-br from-neutral-50 to-neutral-100/50 rounded-lg border-2 border-dashed border-neutral-300 text-center"
    >
      <mat-icon class="!text-[40px] !w-10 !h-10 text-neutral-400 mb-2">inventory_2</mat-icon>
      <p class="text-xs text-neutral-500 font-medium">
        No items yet — click the + button to add one {{ field.label }}
      </p>
    </div>

    <div class="space-y-3 mt-3">
      <div
        *ngFor="let item of arrayControl?.controls; let i = index"
        class="group relative bg-gradient-to-br from-white to-neutral-50/30 border border-neutral-200 rounded-lg p-4 shadow-sm hover:shadow-[0_4px_16px_rgba(15,23,42,0.08)] hover:border-primary-200 transition-all duration-300 animate-[slideUp_0.3s_ease-out]"
      >
        <div class="absolute top-3 right-3 flex items-center gap-2">
          <span
            class="px-2.5 py-1 text-[10px] font-bold text-primary-700 bg-gradient-to-r from-primary-100 to-primary-50 rounded-full border border-primary-200 shadow-sm"
          >
            #{{ i + 1 }}
          </span>
          <button
            type="button"
            (click)="removeArrayItem(i)"
            class="flex items-center justify-center w-8 h-8 rounded-lg text-red-600 hover:bg-red-50 hover:scale-105 transition-all duration-200"
          >
            <mat-icon class="!text-[18px]">delete</mat-icon>
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pr-20">
          <ng-container *ngFor="let child of field.children">
            <ng-container *ngIf="isChildFieldVisible(child, getArrayItemAsFormGroup(i))">
              <app-inputs [field]="child" [group]="getArrayItemAsFormGroup(i)"></app-inputs>
            </ng-container>
          </ng-container>
        </div>
      </div>
    </div>

    <div class="flex justify-end mt-4">
      <button
        type="button"
        (click)="addArrayItem()"
        class="group flex items-center justify-center px-4 h-9 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white transition-all duration-300 shadow-[0_2px_8px_rgba(53,135,178,0.3)] hover:shadow-[0_4px_12px_rgba(53,135,178,0.4)] hover:scale-105"
      >
        <mat-icon class="!text-[20px] transition-transform duration-300 group-hover:rotate-90"
          >add</mat-icon
        >Add more
      </button>
    </div>
  </ng-container>

  <!-- GROUP -->
  <ng-container *ngIf="field.type === 'group'">
    <div class="mb-6">
      <div
        class="mb-4 p-4 bg-gradient-to-r from-primary-50/30 to-transparent rounded-xl border-l-4 border-primary-300"
      >
        <div class="text-base font-bold text-neutral-900">{{ field.label }}</div>
        <div class="text-sm text-neutral-600 mt-0.5" *ngIf="field.hint">{{ field.hint }}</div>
      </div>

      <div
        class="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-5"
      >
        <ng-container *ngFor="let child of field.children">
          <ng-container *ngIf="isChildFieldVisible(child, getGroupAsFormGroup())">
            <app-inputs [field]="child" [group]="getGroupAsFormGroup()"></app-inputs>
          </ng-container>
        </ng-container>
      </div>
    </div>
  </ng-container>

  <!-- FILE -->
  <ng-container *ngIf="field.type === 'file'">
    <div class="modern-input-wrapper">
      <!-- Always show label for context -->
      <div class="flex items-center justify-between mb-3">
        <div>
          <div class="text-sm font-semibold text-neutral-900 flex items-center gap-2">
            <mat-icon class="!text-[18px] !w-[18px] !h-[18px] text-primary-600"
              >cloud_upload</mat-icon
            >
            {{ field.label }}
            <span *ngIf="field.validators?.length" class="text-red-500">*</span>
          </div>
          <p class="text-xs text-neutral-500 mt-1 flex items-center gap-1.5">
            <mat-icon class="!text-[12px] !w-3 !h-3">info</mat-icon>
            {{
              field.hint ||
                'Max ' +
                  (field.maxFileSizeMb || 5) +
                  'MB, ' +
                  (field.acceptedFormats || 'PDF, PNG, JPG')
            }}
          </p>
        </div>
      </div>

      <!-- Upload zone - only show when no file is uploaded and not uploading -->
      <ng-container *ngIf="!control?.value && !uploading">
        <label
          class="file-drop-zone relative flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-2xl cursor-pointer overflow-hidden group bg-gradient-to-br from-primary-50/40 via-white to-primary-50/30 backdrop-blur-sm transition-all duration-300 hover:border-primary-400 hover:shadow-lg hover:shadow-primary-500/10"
          [ngClass]="{
            'border-neutral-300': !control?.invalid && !control?.touched,
            'border-primary-400 bg-primary-50/50': !uploading && !control?.invalid,
            'border-red-400 bg-red-50/30': control?.invalid && control?.touched,
            'border-green-400 bg-green-50/30': control?.valid && control?.touched && !uploading,
          }"
        >
          <!-- Background gradient overlay -->
          <div
            class="absolute inset-0 bg-gradient-to-tr from-primary-500/5 via-transparent to-primary-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          ></div>

          <input
            type="file"
            (change)="onFileChange($event)"
            [attr.multiple]="field.multiple ? '' : null"
            class="hidden"
          />

          <div class="relative z-10 flex flex-col items-center justify-center text-center px-6">
            <!-- Upload Icon  -->
            <div
              class="flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white mb-3 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg shadow-primary-500/30"
            >
              <mat-icon class="!text-[28px] !w-7 !h-7">cloud_upload</mat-icon>
            </div>

            <div class="space-y-1">
              <p
                class="text-sm font-semibold text-neutral-700 group-hover:text-primary-700 transition-colors"
              >
                <span class="text-primary-600">Click to upload</span> or drag and drop
              </p>
              <p class="text-xs text-neutral-500">
                {{ field.acceptedFormats || 'PDF, PNG, JPG' }} (max
                {{ field.maxFileSizeMb || 5 }}MB)
              </p>
            </div>
          </div>

          <div
            class="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style="
              background: linear-gradient(
                45deg,
                transparent 48%,
                rgba(53, 135, 178, 0.2) 50%,
                transparent 52%
              );
              background-size: 20px 20px;
              animation: borderShimmer 3s linear infinite;
            "
          ></div>
        </label>
      </ng-container>

      <div *ngIf="uploading" class="mt-4 animate-[slideUp_0.4s_ease-out]">
        <div class="relative w-full h-2.5 bg-neutral-100 rounded-full overflow-hidden shadow-inner">
          <div
            class="absolute top-0 left-0 h-full bg-gradient-to-r from-primary-500 via-primary-600 to-primary-500 transition-all duration-500 ease-out rounded-full shadow-lg shadow-primary-500/50"
            [style.width.%]="progress"
            style="background-size: 200% 100%; animation: gradientShift 2s ease infinite"
          ></div>
        </div>
        <div class="flex items-center justify-between mt-2.5 text-xs">
          <span class="text-neutral-600 font-medium flex items-center gap-1.5">
            <mat-icon class="!text-[14px] !w-3.5 !h-3.5 animate-spin text-primary-600"
              >refresh</mat-icon
            >
            Uploading...
          </span>
          <span class="text-primary-700 font-bold">{{ progress }}%</span>
        </div>
      </div>

      <!-- Uploaded Files -->
      <div
        *ngIf="control?.value && !uploading"
        class="mt-4 space-y-3 animate-[slideUp_0.4s_ease-out]"
      >
        <div *ngIf="field.multiple" class="space-y-2.5">
          <div
            *ngFor="let f of control?.value; let i = index"
            class="group flex items-center gap-3 p-3.5 rounded-xl border border-neutral-200/60 bg-gradient-to-br from-white/90 via-neutral-50/50 to-white/80 backdrop-blur-sm hover:border-primary-300 hover:shadow-md hover:shadow-primary-500/10 transition-all duration-300 animate-[scaleIn_0.3s_ease-out]"
            [style.animation-delay.ms]="i * 100"
          >
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 group-hover:scale-105 transition-transform duration-300"
            >
              <mat-icon class="!text-[20px] !w-5 !h-5">insert_drive_file</mat-icon>
            </div>

            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-neutral-800 truncate">
                {{ cleanFileName(f.fileName) }}
              </p>
              <p class="text-xs text-neutral-500 mt-0.5">
                {{ (f.size / 1024 / 1024).toFixed(2) }}MB
              </p>
            </div>

            <div
              class="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity"
            >
              <button
                type="button"
                (click)="previewFile(f)"
                class="p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 hover:scale-110"
                title="Preview"
              >
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">visibility</mat-icon>
              </button>
              <button
                type="button"
                (click)="downloadFile(f)"
                class="p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 hover:scale-110"
                title="Download"
              >
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">file_download</mat-icon>
              </button>
              <button
                type="button"
                (click)="removeFile(f)"
                class="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-110"
                title="Remove"
              >
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">close</mat-icon>
              </button>
            </div>
          </div>

          <label
            class="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-primary-300 rounded-xl bg-primary-50/30 hover:bg-primary-50 hover:border-primary-400 cursor-pointer transition-all duration-200 group"
          >
            <input
              type="file"
              (change)="onFileChange($event)"
              [attr.multiple]="field.multiple ? '' : null"
              class="hidden"
            />
            <mat-icon
              class="!text-[20px] !w-5 !h-5 text-primary-600 group-hover:rotate-90 transition-transform duration-300"
              >add</mat-icon
            >
            <span class="text-sm font-medium text-primary-700">Add another file</span>
          </label>
        </div>

        <!-- Single file with replace option -->
        <div *ngIf="!field.multiple" class="space-y-2.5">
          <div
            class="flex items-center gap-3 p-3.5 rounded-xl border border-neutral-200/60 bg-gradient-to-br from-white/90 via-neutral-50/50 to-white/80 backdrop-blur-sm"
          >
            <div
              class="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-200 text-green-700"
            >
              <mat-icon class="!text-[20px] !w-5 !h-5">insert_drive_file</mat-icon>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-neutral-800 truncate">
                {{ cleanFileName(control?.value?.fileName || control?.value) }}
              </p>
              <p class="text-xs text-neutral-500 mt-0.5" *ngIf="control?.value?.size">
                {{ (control?.value?.size / 1024 / 1024).toFixed(2) }}MB
              </p>
            </div>
            <div class="flex items-center gap-1">
              <button
                type="button"
                (click)="previewFile(control?.value)"
                class="p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                title="Preview"
              >
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">visibility</mat-icon>
              </button>
              <button
                type="button"
                (click)="downloadFile(control?.value)"
                class="p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                title="Download"
              >
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">file_download</mat-icon>
              </button>
              <button
                type="button"
                (click)="removeFile(control?.value)"
                class="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                title="Remove"
              >
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">close</mat-icon>
              </button>
            </div>
          </div>

          <!-- Replace file button -->
          <label
            class="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-primary-300 rounded-xl bg-primary-50/30 hover:bg-primary-50 hover:border-primary-400 cursor-pointer transition-all duration-200 group"
          >
            <input type="file" (change)="onFileChange($event)" class="hidden" />
            <mat-icon
              class="!text-[20px] !w-5 !h-5 text-primary-600 group-hover:scale-110 transition-transform duration-300"
              >sync</mat-icon
            >
            <span class="text-sm font-medium text-primary-700">Replace file</span>
          </label>
        </div>
      </div>

      <div
        *ngIf="control?.invalid && control?.touched && !field.validateEndpoint"
        class="mt-3 animate-[slideInLeft_0.3s_ease-out]"
      >
        <p class="text-xs text-red-600 flex items-center gap-1.5 font-medium">
          {{ getErrorMessage() }}
        </p>
      </div>
    </div>
  </ng-container>

  <!-- TEXT / VALIDATE / COMPANY NAME -->
  <ng-container
    *ngIf="
      field.type !== 'array' &&
      field.type !== 'file' &&
      field.type !== 'group' &&
      field.type !== 'templateDownload'
    "
  >
    <div class="w-full floating-label-wrapper">
      <!-- input wrapper is relative so we can absolutely position the validate button -->
      <!-- Simple design for numberOfShares -->
      <div *ngIf="field.name === 'numberOfShares'" class="mb-4">
        <label [for]="field.name" class="block text-sm font-semibold text-gray-700 mb-2">
          {{ field.label }}
          <span *ngIf="field.validators?.length" class="text-red-500 ml-0.5">*</span>
        </label>
        <input
          type="number"
          [value]="control?.value ?? 0"
          [id]="field.name"
          [disabled]="control?.disabled"
          (input)="onManualInput($event, field.name)"
          (blur)="control?.markAsTouched()"
          placeholder="Enter number of shares"
          class="block w-full px-4 py-3 text-base font-medium text-gray-900 bg-white border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          [ngClass]="{
            'border-gray-300 hover:border-gray-400': !control?.invalid && !control?.disabled,
            'border-red-400 bg-red-50': control?.invalid && control?.touched,
            'border-green-400': control?.valid && control?.touched && !control?.disabled,
          }"
        />
        <p *ngIf="field.hint && !control?.invalid" class="mt-1 text-xs text-gray-500">
          {{ field.hint }}
        </p>
        <p
          *ngIf="
            control?.invalid &&
            control?.touched &&
            !field.validateEndpoint &&
            field.type !== 'validate'
          "
          class="mt-1 text-xs text-red-600 font-medium"
        >
          <mat-icon class="inline-block !text-[14px] !w-3.5 !h-3.5 align-text-bottom"
            >error</mat-icon
          >
          {{ getErrorMessage() }}
        </p>
      </div>

      <!-- Standard floating label for other inputs -->
      <div
        *ngIf="
          field.name !== 'numberOfShares' &&
          field.type !== 'textarea' &&
          field.type !== 'select' &&
          field.type !== 'date' &&
          field.type !== 'time'
        "
        class="relative"
      >
        <input
          #inputElement
          [formControlName]="field.name"
          [type]="field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'"
          [placeholder]="' '"
          [id]="field.name"
          (keydown)="onSuggestionKeyDown($event)"
          class="floating-input w-full pr-28 px-4 py-3.5 pt-6 border-2 rounded-xl transition-all duration-300 peer focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-500 disabled:cursor-not-allowed"
          [ngClass]="{
            'border-neutral-200 hover:border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20':
              !control?.invalid && !control?.disabled,
            'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20 animate-[shake_0.4s_ease-in-out]':
              control?.invalid && control?.touched,
            'border-green-400 focus:border-green-500 focus:ring-4 focus:ring-green-500/20':
              control?.valid && control?.touched && !control?.disabled,
          }"
        />
        <label
          [for]="field.name"
          class="floating-label absolute left-4 text-neutral-700 transition-all duration-300 pointer-events-none"
          [ngClass]="{
            'text-primary-600': !control?.invalid && !control?.touched,
            'text-red-500': control?.invalid && control?.touched,
            'text-green-600': control?.valid && control?.touched && !control?.disabled,
          }"
        >
          {{ field.label }}
          <span *ngIf="field.validators?.length" class="text-red-500 ml-0.5">*</span>
        </label>

        <div class="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1">
          <mat-icon
            *ngIf="control?.valid && control?.touched && !control?.disabled"
            class="!text-[20px] !w-5 !h-5 text-green-500 animate-[scaleIn_0.3s_ease-out]"
          >
            check_circle
          </mat-icon>
          <mat-icon
            *ngIf="control?.invalid && control?.touched"
            class="!text-[20px] !w-5 !h-5 text-red-500 animate-[shake_0.4s_ease-in-out]"
          >
            error
          </mat-icon>
        </div>

        <button
          *ngIf="(field.type === 'validate' || field.validateEndpoint) && !field.readOnly"
          type="button"
          (click)="onValidateClick()"
          [disabled]="validating || !control?.value"
          class="absolute right-1 top-1/2 -translate-y-1/2 z-20 inline-flex items-center gap-2 px-3 py-1.5 rounded-md font-semibold text-sm bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed height-90"
          title="Validate Information"
        >
          <mat-icon *ngIf="!validating" class="!text-[16px]">verified_user</mat-icon>
          <mat-icon *ngIf="validating" class="!text-[16px] animate-spin">refresh</mat-icon>
          <span class="text-xs">{{ validating ? 'Validating' : 'Validate' }}</span>
        </button>
      </div>

      <div *ngIf="field.type === 'date'" class="relative">
        <input
          [matDatepicker]="picker"
          [formControlName]="field.name"
          [id]="field.name"
          [placeholder]="' '"
          [attr.aria-label]="field.label"
          [min]="today"
          [max]="field.maxDate"
          (click)="picker.open()"
          readonly
          class="floating-input w-full pr-12 px-4 py-3.5 pt-6 border-2 rounded-xl transition-all duration-300 peer focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-500 disabled:cursor-pointer cursor-pointer"
          [ngClass]="{
            'border-neutral-200 hover:border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20':
              !control?.invalid && !control?.disabled,
            'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20 animate-[shake_0.4s_ease-in-out]':
              control?.invalid && control?.touched,
            'border-green-400 focus:border-green-500 focus:ring-4 focus:ring-green-500/20':
              control?.valid && control?.touched && !control?.disabled,
          }"
        />
        <label
          [for]="field.name"
          class="floating-label absolute left-4 text-neutral-700 transition-all duration-300 pointer-events-none"
          [ngClass]="{
            'text-primary-600': !control?.invalid && !control?.touched,
            'text-red-500': control?.invalid && control?.touched,
            'text-green-600': control?.valid && control?.touched && !control?.disabled,
          }"
        >
          {{ field.label }}
          <span *ngIf="field.validators?.length" class="text-red-500 ml-0.5">*</span>
        </label>

        <mat-datepicker-toggle
          [for]="picker"
          [disabled]="control?.disabled"
          class="absolute right-2 top-1/2 -translate-y-1/2 z-10"
        >
          <mat-icon matDatepickerToggleIcon class="text-neutral-500">calendar_month</mat-icon>
        </mat-datepicker-toggle>
        <mat-datepicker #picker [disabled]="control?.disabled"></mat-datepicker>
      </div>

      <!-- Time -->
      <div *ngIf="field.type === 'time'" class="relative">
        <select
          [formControlName]="field.name"
          [id]="field.name"
          class="floating-input w-full px-4 py-3.5 pt-6 border-2 rounded-xl transition-all duration-300 peer focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-500 disabled:cursor-pointer appearance-none bg-white"
          [ngClass]="{
            'border-neutral-200 hover:border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20':
              !control?.invalid && !control?.disabled,
            'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20':
              control?.invalid && control?.touched,
            'border-green-400 focus:border-green-500 focus:ring-4 focus:ring-green-500/20':
              control?.valid && control?.touched && !control?.disabled,
          }"
        >
          <option [ngValue]="null" disabled>Select time</option>
          <option *ngFor="let time of timeOptions" [ngValue]="time.value">
            {{ time.label }}
          </option>
        </select>
        <label
          [for]="field.name"
          class="floating-label absolute left-4 text-neutral-700 transition-all duration-300 pointer-events-none"
          [ngClass]="{
            'text-primary-600': !control?.invalid && !control?.touched,
            'text-red-500': control?.invalid && control?.touched,
            'text-green-600': control?.valid && control?.touched && !control?.disabled,
          }"
        >
          {{ field.label }}
          <span *ngIf="field.validators?.length" class="text-red-500 ml-0.5">*</span>
        </label>
        <div class="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </div>
      </div>

      <!-- Textarea -->
      <div *ngIf="field.type === 'textarea'" class="relative">
        <textarea
          [formControlName]="field.name"
          rows="4"
          [placeholder]="' '"
          [id]="field.name"
          class="floating-input w-full px-4 py-3.5 pt-6 border-2 rounded-xl transition-all duration-300 resize-none peer focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-500 disabled:cursor-not-allowed"
          [ngClass]="{
            'border-neutral-200 hover:border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20':
              !control?.invalid && !control?.disabled,
            'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20':
              control?.invalid && control?.touched,
            'border-green-400 focus:border-green-500 focus:ring-4 focus:ring-green-500/20':
              control?.valid && control?.touched && !control?.disabled,
          }"
        ></textarea>
        <label
          [for]="field.name"
          class="floating-label absolute left-4 text-neutral-700 transition-all duration-300 pointer-events-none"
          [ngClass]="{
            'text-primary-600': !control?.invalid && !control?.touched,
            'text-red-500': control?.invalid && control?.touched,
            'text-green-600': control?.valid && control?.touched && !control?.disabled,
          }"
        >
          {{ field.label }}
          <span *ngIf="field.validators?.length" class="text-red-500 ml-0.5">*</span>
        </label>
      </div>

      <!-- Single Select -->
      <div
        *ngIf="field.type === 'select' && !field.multiple"
        class="relative"
        [ngClass]="openSingleSelectDropdown === field.name ? 'z-[100000]' : 'z-10'"
        (appClickOutside)="closeSingleSelectDropdown(field.name)"
        appClickOutside
      >
        <select [formControlName]="field.name" [id]="field.name" class="hidden">
          <option value=""></option>
          <option *ngFor="let opt of getSelectOptions()" [value]="opt.value">
            {{ opt.label | removeUnderscore }}
          </option>
        </select>

        <div
          class="floating-input w-full px-4 py-3.5 pt-6 border-2 rounded-xl transition-all duration-300 bg-white cursor-pointer peer focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-500 disabled:cursor-not-allowed"
          [ngClass]="{
            'border-neutral-200 hover:border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20':
              !control?.invalid && !control?.disabled,
            'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20':
              control?.invalid && control?.touched,
            'border-green-400 focus:border-green-500 focus:ring-4 focus:ring-green-500/20':
              control?.valid && control?.touched && !control?.disabled,
          }"
          (click)="toggleSingleSelectDropdown(field.name)"
          [attr.tabindex]="control?.disabled ? -1 : 0"
          (keydown.enter)="toggleSingleSelectDropdown(field.name)"
          (keydown.space)="toggleSingleSelectDropdown(field.name)"
        >
          <span *ngIf="!control?.value" class="text-neutral-400">{{
            field.placeholder || 'Select an option'
          }}</span>
          <span *ngIf="control?.value" class="text-neutral-900">{{
            getSelectedLabel(field.name)
          }}</span>
        </div>

        <div
          *ngIf="openSingleSelectDropdown === field.name"
          class="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-neutral-200 rounded-xl shadow-lg z-[99999] max-h-80 overflow-hidden flex flex-col"
        >
          <!-- Search Input -->
          <div class="p-3 border-b border-neutral-200 sticky top-0 bg-white">
            <div class="relative">
              <mat-icon
                class="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 !text-[18px] !w-[18px] !h-[18px]"
                >search</mat-icon
              >
              <input
                type="text"
                placeholder="Search options..."
                (input)="onSelectSearch($event, field.name)"
                (click)="$event.stopPropagation()"
                class="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm"
              />
            </div>
          </div>

          <!-- Options List -->
          <div class="overflow-y-auto max-h-64">
            <div
              *ngFor="let opt of getFilteredSelectOptions(field.name)"
              (click)="selectSingleOption(field.name, opt.value)"
              class="px-4 py-3 hover:bg-neutral-50 cursor-pointer transition-colors"
              [ngClass]="{
                'bg-primary-50 text-primary-700 font-medium': control?.value === opt.value,
              }"
            >
              {{ opt.label | removeUnderscore }}
            </div>
            <div
              *ngIf="getFilteredSelectOptions(field.name).length === 0"
              class="px-4 py-8 text-center text-neutral-500 text-sm"
            >
              No options found
            </div>
          </div>
        </div>

        <label
          [for]="field.name"
          class="floating-label absolute left-4 top-3.5 text-[11px] font-medium text-neutral-700 transition-all duration-300 pointer-events-none"
          [ngClass]="{
            'peer-focus:text-primary-600': !control?.invalid,
            'text-red-500': control?.invalid && control?.touched,
            'text-green-600': control?.valid && control?.touched && !control?.disabled,
          }"
        >
          {{ field.label }}
          <span *ngIf="field.validators?.length" class="text-red-500 ml-0.5">*</span>
        </label>
        <mat-icon
          class="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
          [ngClass]="{ 'rotate-180': openSingleSelectDropdown === field.name }"
          >expand_more</mat-icon
        >
      </div>

      <!-- Multi Select -->
      <div
        *ngIf="field.type === 'select' && field.multiple"
        class="relative"
        [ngClass]="openMultiSelectDropdown === field.name ? 'z-[100000]' : 'z-10'"
        (appClickOutside)="closeMultiSelectDropdown(field.name)"
        appClickOutside
      >
        <select [formControlName]="field.name" [id]="field.name" multiple class="hidden">
          <option *ngFor="let opt of getSelectOptions()" [value]="opt.value">
            {{ opt.label | removeUnderscore }}
          </option>
        </select>

        <div
          class="floating-input w-full px-4 py-3.5 pt-6 border-2 rounded-xl transition-all duration-300 bg-white cursor-pointer peer focus:outline-none disabled:bg-neutral-50 disabled:text-neutral-500 disabled:cursor-not-allowed"
          [ngClass]="{
            'border-neutral-200 hover:border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20':
              !control?.invalid && !control?.disabled,
            'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20':
              control?.invalid && control?.touched,
            'border-green-400 focus:border-green-500 focus:ring-4 focus:ring-green-500/20':
              control?.valid && control?.touched && !control?.disabled,
          }"
          (click)="toggleMultiSelectDropdown(field.name)"
          [attr.tabindex]="control?.disabled ? -1 : 0"
          (keydown.enter)="toggleMultiSelectDropdown(field.name)"
          (keydown.space)="toggleMultiSelectDropdown(field.name)"
        >
          <div class="flex flex-wrap gap-2">
            <span *ngIf="getSelectedLabels(field.name).length === 0" class="text-neutral-400">
              Select options...
            </span>
            <span
              *ngFor="let label of getSelectedLabels(field.name)"
              class="inline-flex items-center gap-1 bg-primary-100 text-primary-700 px-2 py-1 rounded text-sm"
            >
              {{ label | removeUnderscore }}
              <button
                type="button"
                *ngIf="!control?.disabled"
                (click)="removeMultiSelectItem(field.name, label); $event.stopPropagation()"
                class="hover:text-primary-900"
              >
                ✕
              </button>
            </span>
          </div>
        </div>

        <div
          *ngIf="openMultiSelectDropdown === field.name"
          class="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-neutral-200 rounded-xl shadow-lg z-[99999] max-h-80 overflow-hidden flex flex-col"
        >
          <!-- Search Input -->
          <div class="p-3 border-b border-neutral-200 sticky top-0 bg-white">
            <div class="relative">
              <mat-icon
                class="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 !text-[18px] !w-[18px] !h-[18px]"
                >search</mat-icon
              >
              <input
                type="text"
                placeholder="Search options..."
                (input)="onSelectSearch($event, field.name)"
                (click)="$event.stopPropagation()"
                class="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm"
              />
            </div>
          </div>

          <!-- Options List -->
          <div class="overflow-y-auto max-h-64">
            <div *ngFor="let opt of getFilteredSelectOptions(field.name)">
              <label
                class="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  [checked]="isMultiSelectItemSelected(field.name, opt.value)"
                  (change)="toggleMultiSelectItem(field.name, opt.value, opt.label)"
                  class="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span class="text-neutral-700">{{ opt.label | removeUnderscore }}</span>
              </label>
            </div>
            <div
              *ngIf="getFilteredSelectOptions(field.name).length === 0"
              class="px-4 py-8 text-center text-neutral-500 text-sm"
            >
              No options found
            </div>
          </div>
        </div>

        <label
          [for]="field.name"
          class="floating-label absolute left-4 top-3.5 text-[11px] font-medium text-neutral-700 transition-all duration-300 pointer-events-none mb-4"
          [ngClass]="{
            'peer-focus:text-primary-600': !control?.invalid,
            'text-red-500': control?.invalid && control?.touched,
            'text-green-600': control?.valid && control?.touched && !control?.disabled,
          }"
        >
          {{ field.label }}
          <span *ngIf="field.validators?.length" class="text-red-500 ml-0.5">*</span>
        </label>
        <mat-icon
          class="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none transition-transform"
          [ngClass]="{ 'rotate-180': openMultiSelectDropdown === field.name }"
        >
          expand_more
        </mat-icon>
      </div>

      <div class="mt-2 min-h-[20px]">
        <p
          *ngIf="field.hint && !control?.invalid && !control?.valid"
          class="text-xs text-neutral-500 flex items-center gap-1.5 animate-[fadeIn_0.3s_ease-out]"
        >
          <mat-icon class="!text-[14px] !w-3.5 !h-3.5">info</mat-icon>
          {{ field.hint }}
        </p>

        <p
          *ngIf="
            control?.invalid &&
            control?.touched &&
            field.type !== 'validate' &&
            !field.validateEndpoint
          "
          class="text-xs text-red-600 flex items-center gap-1.5 font-medium animate-[slideInLeft_0.3s_ease-out]"
        >
          {{ getErrorMessage() }}
        </p>
      </div>
    </div>

    <!-- CHECKBOX -->
    <div *ngIf="field.type === 'checkbox'" class="relative">
      <label
        class="flex items-start gap-3 cursor-pointer group p-4 rounded-xl border-2 transition-all duration-300 hover:bg-neutral-50"
        [ngClass]="{
          'border-neutral-200 hover:border-primary-300': !control?.invalid,
          'border-red-300 bg-red-50/30': control?.invalid && control?.touched,
        }"
      >
        <div class="relative flex items-center justify-center flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            [formControlName]="field.name"
            [id]="field.name"
            class="peer w-5 h-5 rounded border-2 border-neutral-300 text-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 cursor-pointer"
          />
          <mat-icon
            class="absolute !text-[16px] !w-4 !h-4 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity duration-200"
          >
            check
          </mat-icon>
        </div>
        <div class="flex-1">
          <span
            class="text-sm font-medium text-neutral-900 group-hover:text-primary-700 transition-colors"
          >
            {{ field.label }}
            <span *ngIf="field.validators?.length" class="text-red-500">*</span>
          </span>
          <p *ngIf="field.hint" class="text-xs text-neutral-600 mt-1">
            {{ field.hint }}
          </p>
        </div>
      </label>
      <p
        *ngIf="control?.invalid && control?.touched && !field.validateEndpoint"
        class="mt-2 text-xs text-red-600 flex items-center gap-1.5 animate-[slideDown_0.2s_ease-out]"
      >
        {{ getErrorMessage() }}
      </p>
    </div>

    <div *ngIf="validatedData && field.validateKind !== 'localCompany'" class="mt-4 animate-[scaleIn_0.4s_ease-out]">
      <div
        class="relative p-5 rounded-2xl border border-green-200/60 bg-gradient-to-br from-green-50/90 via-white/80 to-green-50/70 backdrop-blur-sm shadow-lg shadow-green-500/10 overflow-hidden"
      >
        <div
          class="absolute inset-0 opacity-30"
          style="
            background-image: radial-gradient(
              circle at 20px 20px,
              rgba(34, 197, 94, 0.1) 1px,
              transparent 1px
            );
            background-size: 40px 40px;
          "
        ></div>

        <div class="relative z-10 flex items-start gap-4">
          <div
            class="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30 animate-[pulse_2s_ease-in-out_infinite]"
          >
            <mat-icon class="!text-[24px] !w-6 !h-6">check_circle</mat-icon>
          </div>

          <div class="flex-1 space-y-2">
            <h4 class="font-bold text-lg text-neutral-900">
              {{
                validatedData.name ||
                  validatedData.fullName ||
                  validatedData.companyName ||
                  validatedData.companyinfo?.value?.name
              }}
            </h4>

            <div class="flex items-center gap-2 text-sm text-neutral-600">
              <mat-icon class="!text-[16px] !w-4 !h-4 text-green-600">badge</mat-icon>
              <span class="font-mono font-semibold text-green-800">
                {{
                  validatedData.tinNumber ||
                    validatedData.identificationNumber ||
                    validatedData.registrationNumber ||
                    validatedData.companyinfo?.value?.companyNumber
                }}
              </span>
            </div>

            <div
              *ngIf="validatedData.address"
              class="flex items-start gap-2 text-sm text-neutral-700 pt-1"
            >
              <mat-icon class="!text-[16px] !w-4 !h-4 text-neutral-500 mt-0.5"
                >location_on</mat-icon
              >
              <span class="leading-relaxed">{{ validatedData.address }}</span>
            </div>

            <!-- Additional fields if available -->
            <div
              *ngIf="validatedData.email || validatedData.phone"
              class="flex flex-wrap gap-4 pt-2"
            >
              <div
                *ngIf="validatedData.email"
                class="flex items-center gap-1.5 text-xs text-neutral-600"
              >
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5">email</mat-icon>
                {{ validatedData.email }}
              </div>
              <div
                *ngIf="validatedData.phone"
                class="flex items-center gap-1.5 text-xs text-neutral-600"
              >
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5">phone</mat-icon>
                {{ validatedData.phone }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      *ngIf="
        validatedData &&
        field.validateKind === 'localCompany' &&
        (validatedData.companyinfo ||
          validatedData.directors ||
          validatedData.shareholders ||
          validatedData.seccretary)
      "
      class="mt-4 animate-[scaleIn_0.4s_ease-out]"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          *ngIf="validatedData.companyinfo?.value as ci"
          class="p-5 rounded-2xl border border-neutral-200 bg-white shadow-sm h-full"
        >
          <div class="flex items-center gap-2 mb-3">
            <h5 class="text-sm font-bold text-neutral-900">Company Information</h5>
          </div>
          <dl class="space-y-2 text-sm">
            <div *ngIf="ci.name">
              <dt class="text-xs text-neutral-500">Name</dt>
              <dd class="font-medium text-neutral-900">{{ ci.name }}</dd>
            </div>
            <div *ngIf="ci.companyNumber">
              <dt class="text-xs text-neutral-500">Company Number</dt>
              <dd class="font-mono text-neutral-900">{{ ci.companyNumber }}</dd>
            </div>
            <div *ngIf="ci.type">
              <dt class="text-xs text-neutral-500">Type</dt>
              <dd class="text-neutral-900">{{ ci.type }}</dd>
            </div>
            <div *ngIf="ci.city">
              <dt class="text-xs text-neutral-500">City</dt>
              <dd class="text-neutral-900">{{ ci.city }}</dd>
            </div>
            <div *ngIf="ci.physicalAddress">
              <dt class="text-xs text-neutral-500">Physical Address</dt>
              <dd class="text-neutral-900">{{ ci.physicalAddress }}</dd>
            </div>
            <div *ngIf="ci.postalAddress">
              <dt class="text-xs text-neutral-500">Postal Address</dt>
              <dd class="text-neutral-900">{{ ci.postalAddress }}</dd>
            </div>
            <div *ngIf="ci.email">
              <dt class="text-xs text-neutral-500">Email</dt>
              <dd class="text-neutral-900 break-all">{{ ci.email }}</dd>
            </div>
            <div *ngIf="ci.mobileNumber || ci.telNumber">
              <dt class="text-xs text-neutral-500">Phone</dt>
              <dd class="text-neutral-900">{{ ci.mobileNumber || ci.telNumber }}</dd>
            </div>
          </dl>
        </div>
        <div
          *ngIf="validatedData.shareholders?.value?.length"
          class="p-5 rounded-2xl border border-neutral-200 bg-white shadow-sm h-full"
        >
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <h5 class="text-sm font-bold text-neutral-900">Shareholders</h5>
            </div>
            <span
              class="text-xs font-semibold text-neutral-500 px-2 py-0.5 rounded-full bg-neutral-100"
              >{{ validatedData.shareholders.value.length }}</span
            >
          </div>
          <ul class="divide-y divide-neutral-100">
            <li *ngFor="let s of validatedData.shareholders.value" class="py-3 text-sm space-y-1">
              <div class="flex items-center justify-between gap-2">
                <div class="font-medium text-neutral-900">
                  {{ (s.names || '') + ' ' + (s.surname || '') }}
                </div>
                <div class="flex flex-wrap gap-1 text-[10px]">
                  <span class="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 font-semibold"
                    >Ordinary shares: {{ s.ordinaryshares || 0 }}</span
                  >
                  <span class="px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 font-semibold"
                    >Preference shares: {{ s.prefereceShares ?? s.preferenceShares ?? 0 }}</span
                  >
                </div>
              </div>
              <div class="font-mono text-xs text-neutral-600">{{ s.id_no?.trim() }}</div>
              <div
                *ngIf="s.email"
                class="flex items-center gap-1.5 text-xs text-neutral-700 break-all"
              >
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 text-neutral-400">email</mat-icon>
                {{ s.email }}
              </div>
              <div *ngIf="s.address" class="flex items-start gap-1.5 text-xs text-neutral-600">
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 text-neutral-400 mt-0.5"
                  >location_on</mat-icon
                >
                <span>{{ s.address }}</span>
              </div>
            </li>
          </ul>
        </div>

        <div
          *ngIf="validatedData.directors?.value?.length"
          class="p-5 rounded-2xl border border-neutral-200 bg-white shadow-sm h-full"
        >
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <h5 class="text-sm font-bold text-neutral-900">Directors</h5>
            </div>
            <span
              class="text-xs font-semibold text-neutral-500 px-2 py-0.5 rounded-full bg-neutral-100"
              >{{ validatedData.directors.value.length }}</span
            >
          </div>
          <ul class="divide-y divide-neutral-100">
            <li *ngFor="let d of validatedData.directors.value" class="py-3 text-sm space-y-1">
              <div class="font-medium text-neutral-900">
                {{ (d.names || '') + ' ' + (d.surname || '') }}
              </div>
              <div class="font-mono text-xs text-neutral-600">{{ d.id_no?.trim() }}</div>
              <div
                *ngIf="d.email"
                class="flex items-center gap-1.5 text-xs text-neutral-700 break-all"
              >
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 text-neutral-400">email</mat-icon>
                {{ d.email }}
              </div>
              <div *ngIf="d.address" class="flex items-start gap-1.5 text-xs text-neutral-600">
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 text-neutral-400 mt-0.5"
                  >location_on</mat-icon
                >
                <span>{{ d.address }}</span>
              </div>
            </li>
          </ul>
        </div>

        <div
          *ngIf="validatedData.seccretary?.value as sec"
          class="p-5 rounded-2xl border border-neutral-200 bg-white shadow-sm h-full"
        >
          <div class="flex items-center gap-2 mb-3">
            <h5 class="text-sm font-bold text-neutral-900">Secretary</h5>
          </div>
          <dl class="space-y-2 text-sm">
            <div>
              <dt class="text-xs text-neutral-500">Name</dt>
              <dd class="font-medium text-neutral-900">
                {{ (sec.names || '') + ' ' + (sec.surname || '') }}
              </dd>
            </div>
            <div *ngIf="sec.id_no">
              <dt class="text-xs text-neutral-500">ID Number</dt>
              <dd class="font-mono text-neutral-900">{{ sec.id_no?.trim() }}</dd>
            </div>
            <div *ngIf="sec.email">
              <dt class="text-xs text-neutral-500">Email</dt>
              <dd class="text-neutral-900 break-all">{{ sec.email }}</dd>
            </div>
            <div *ngIf="sec.address">
              <dt class="text-xs text-neutral-500">Address</dt>
              <dd class="text-neutral-900">{{ sec.address }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <p class="text-xs text-neutral-500 italic px-1 mt-3">
        Company, director and shareholder details have been auto-filled. Identity and share fields
        are locked; you can still edit email if changed, directors positions, shareholder type and upload proof of address.
      </p>
    </div>

    <div
      *ngIf="validateError"
      class="mt-3 p-4 rounded-xl border border-red-200/60 bg-gradient-to-br from-red-50/90 to-white/80 backdrop-blur-sm animate-[shake_0.5s_ease-in-out]"
    >
      <p class="flex items-center gap-2 text-sm text-red-700 font-medium">
        <mat-icon class="!text-[18px] !w-[18px] !h-[18px]">error</mat-icon>
        {{ validateError }}
      </p>
    </div>

    <!-- COMPANY NAME RESERVE -->
    <div
      *ngIf="field.reserveEndpoint && control?.value && control?.value.trim().length > 0"
      class="mt-4"
    >
      <div
        class="relative p-5 rounded-2xl border border-neutral-200/60 bg-gradient-to-br from-white/90 via-neutral-50/50 to-white/80 backdrop-blur-sm shadow-md overflow-hidden"
      >
        <div
          class="absolute inset-0 opacity-0 transition-opacity duration-500"
          [class.opacity-100]="nameChecking"
          style="
            background: linear-gradient(
              45deg,
              transparent 48%,
              rgba(53, 135, 178, 0.05) 50%,
              transparent 52%
            );
            background-size: 30px 30px;
            animation: backgroundShift 1.5s linear infinite;
          "
        ></div>

        <div class="relative z-10 flex items-center justify-between gap-4">
          <div class="flex-1">
            <div
              *ngIf="nameChecking"
              class="flex items-center gap-2.5 animate-[fadeIn_0.3s_ease-out]"
            >
              <div class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100">
                <mat-icon class="!text-[18px] !w-[18px] !h-[18px] text-primary-600 animate-spin"
                  >refresh</mat-icon
                >
              </div>
              <span class="text-sm font-semibold text-neutral-700">Checking availability...</span>
            </div>

            <div
              *ngIf="!nameChecking && nameAvailable"
              class="flex items-center gap-2 animate-[slideInLeft_0.3s_ease-out]"
            >
              <div
                class="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-green-500 to-green-600 shadow-md shadow-green-500/20"
              >
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 text-white">check_circle</mat-icon>
              </div>
              <p class="text-xs font-semibold text-green-700">Name Available!</p>
            </div>

            <div
              *ngIf="!nameChecking && !nameAvailable && control?.value"
              class="flex items-center gap-2 animate-[shake_0.5s_ease-out]"
            >
              <div
                class="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-md shadow-red-500/20"
              >
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 text-white">cancel</mat-icon>
              </div>
              <p class="text-xs font-semibold text-red-700">Name Not Available</p>
            </div>
          </div>
        </div>
      </div>

      <!-- name suggestion -->
      <div *ngIf="suggestions.length" class="mt-3 animate-[slideUp_0.3s_ease-out]">
        <div
          class="relative px-3 py-2.5 rounded-xl border backdrop-blur-sm overflow-hidden"
          [ngClass]="{
            'border-primary-200/60 bg-gradient-to-br from-primary-50/50 via-white/60 to-primary-50/40':
              nameAvailable,
            'border-orange-200/60 bg-gradient-to-br from-orange-50/50 via-white/60 to-orange-50/40':
              !nameAvailable,
          }"
        >
          <div class="relative z-10">
            <div class="flex items-center gap-1.5 mb-2">
              <mat-icon
                class="!text-[16px] !w-4 !h-4"
                [ngClass]="{
                  'text-primary-600': nameAvailable,
                  'text-orange-600': !nameAvailable,
                }"
              >
                {{ nameAvailable ? 'tips_and_updates' : 'lightbulb' }}
              </mat-icon>
              <h4 class="text-xs font-semibold text-neutral-700">
                {{ nameAvailable ? 'Similar Names' : 'Try These Instead' }}
              </h4>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
              <button
                *ngFor="let s of suggestions; let i = index"
                type="button"
                tabindex="0"
                [class.ring-2]="selectedSuggestionIndex === i"
                [class.ring-primary-500]="selectedSuggestionIndex === i"
                class="group flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-white/80 hover:bg-white border border-neutral-200/60 hover:border-primary-300 hover:shadow-sm transition-all duration-200 cursor-pointer animate-[fadeIn_0.2s_ease-out] text-left relative"
                [style.animation-delay.ms]="i * 60"
                (click)="applySuggestion(s.name, i)"
                matTooltip="{{ s.reason || s.category }}"
              >
                <div
                  class="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full font-bold text-[9px]"
                  [ngClass]="{
                    'bg-primary-100 text-primary-700 group-hover:bg-primary-200':
                      s.category === 'variation',
                    'bg-orange-100 text-orange-700 group-hover:bg-orange-200':
                      s.category === 'similar',
                    'bg-green-100 text-green-700 group-hover:bg-green-200':
                      s.category === 'generated',
                  }"
                >
                  {{ i + 1 }}
                </div>

                <span
                  class="flex-1 font-medium"
                  [title]="s.name"
                  [innerHTML]="s.name | highlight: control?.value"
                  [ngClass]="{
                    'text-primary-700': s.category === 'variation',
                    'text-orange-700': s.category === 'similar',
                    'text-green-700': s.category === 'generated',
                  }"
                >
                </span>

                <mat-icon
                  class="!text-[14px] !w-4 !h-4 flex-shrink-0"
                  [ngClass]="{
                    'text-primary-600': s.category === 'variation',
                    'text-orange-600': s.category === 'similar',
                    'text-green-600': s.category === 'generated',
                  }"
                >
                </mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="field.type === 'templateDownload'">
    <div class="modern-input-wrapper">
      <div
        class="relative overflow-hidden rounded-2xl border-2 border-dashed border-primary-200 bg-gradient-to-br from-primary-50/60 via-white to-primary-50/40 p-5 transition-all duration-300 hover:border-primary-300 hover:shadow-md"
      >
        <div
          class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary-100/40 to-transparent rounded-bl-full pointer-events-none"
        ></div>

        <div class="relative z-10">
          <div class="flex items-start gap-3 mb-4">
            <div
              class="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25"
            >
              <mat-icon class="!text-[22px] !w-[22px] !h-[22px]">description</mat-icon>
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="text-sm font-bold text-neutral-900">{{ field.label }}</h4>
              <p class="text-xs text-neutral-500 mt-0.5 leading-relaxed">{{ field.hint }}</p>
            </div>
          </div>

          <button
            type="button"
            (click)="downloadTemplate()"
            class="group inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium text-sm shadow-md shadow-primary-600/25 hover:shadow-lg hover:shadow-primary-600/35 hover:from-primary-700 hover:to-primary-800 active:scale-[0.98] transition-all duration-200"
          >
            <mat-icon class="!text-[20px] !w-5 !h-5 group-hover:animate-bounce">download</mat-icon>
            <span>Download Template</span>
            <span class="text-[10px] font-normal opacity-75 ml-1">(.docx)</span>
          </button>
        </div>
      </div>
    </div>
  </ng-container>
</div>

<!-- File Preview Modal -->
<app-file-preview
  *ngIf="showFilePreview && filePreviewData"
  [file]="filePreviewData"
  [mode]="'modal'"
  [autoOpen]="true"
  (closed)="closeFilePreview()"
></app-file-preview>
